/**
 * yanamas Guidance service — Inbox threads + My World candidates.
 *
 * Backed by saas-core's /api/horton-guidance proxy over the newage
 * engine. A 503 with engine_unavailable means the guidance engine is
 * down — the Inbox renders an honest empty state, never an error wall.
 */

export interface OpenThread {
  id: string
  title: string
  what_horton_heard: string
  possible_lifemap_type: string | null
  status: string
  sensitivity: string
  relevance_score: number
  user_words: string[]
  missing_to_make_actionable: string[]
  connected_node_ids: string[]
  connected_entity_ids: string[]
  converted_node_id: string | null
  created_at: string
  last_touched_at: string
  /** Origin conversation (engine session) — nullable for pre-migration rows. */
  session_id?: string | null
  /** Provenance attached by saas-core (reverse session→chat map). */
  source_chat_id?: string
  source_chat_title?: string
  /** When a snoozed thread returns on its own (Saved-for-later shelf). */
  snoozed_until?: string | null
}

export interface WorldCandidate {
  id: string
  kind: 'person' | 'place'
  name_or_alias: string
  relationship_as_user_stated: string | null
  place_kind_hint: string | null
  aliases: string[]
  status: string
  node_id: string | null
  created_at: string
  /** Origin conversation (engine session) — nullable for pre-migration rows. */
  session_id?: string | null
  /** Provenance attached by saas-core (reverse session→chat map). */
  source_chat_id?: string
  source_chat_title?: string
}

/**
 * A person/place captured this turn that's waiting inside an unapproved
 * ("needs_approval") patch — it isn't a My-People node yet and never entered
 * the noticed-candidate lane, so the chat surfaces it read-only from here so
 * the user can see who yanamas captured before the patch is approved.
 */
export interface HeldPerson {
  kind: 'person' | 'place'
  name_or_alias: string
  relationship_as_user_stated: string | null
  place_kind_hint: string | null
  patch_id: string
  status: string
}

export interface GuidanceInbox {
  threads: OpenThread[]
  world_candidates: WorldCandidate[]
  held_people?: HeldPerson[]
  engineUnavailable?: boolean
}

/** "Doing this would also help achieve X" — a held/saved thought a
 *  just-saved/completed item genuinely moves forward. */
export interface GuidanceConnection {
  item_title: string
  why: string
  thread: {
    id: string
    title: string
    status: string
    session_id: string | null
    source_chat_id?: string
    source_chat_title?: string
  }
}

/** One calibration facet row ("How yanamas helps me"). ``source`` says who
 *  set it: 'explicit' (the user, on the page) or 'learned' (behavioral). */
export interface GuidanceProfileFacet {
  facet: string
  value: string
  source: string
}

export interface GuidanceDomainOverride {
  life_domain: string
  facet: string
  value: string
  source: string
}

export interface GuidanceProfile {
  facets: GuidanceProfileFacet[]
  domain_overrides: GuidanceDomainOverride[]
  engineUnavailable?: boolean
}

/** A behavioral breadcrumb. ``chat_id`` (when known) lets the backend scope
 *  the event to the conversation it happened in. */
export interface GuidanceProfileEvent {
  event_kind: string
  chat_id?: string
  detail?: Record<string, unknown>
}

class HortonGuidanceService {
  private baseUrl = '/api/horton-guidance'

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      ...init,
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      const error = new Error(body.error || `Guidance request failed (${response.status})`)
      ;(error as Error & { engineUnavailable?: boolean }).engineUnavailable =
        body.engine_unavailable === true
      throw error
    }
    return response.json()
  }

  async getInbox(limit = 5, activeChatId?: string): Promise<GuidanceInbox> {
    // The active conversation's held items live in the chat, not the Inbox —
    // pass its chat id so the backend gates them out until it's abandoned or
    // partially Saved.
    const qs = `/inbox?limit=${limit}${activeChatId ? `&active_chat_id=${encodeURIComponent(activeChatId)}` : ''}`
    try {
      return await this.request<GuidanceInbox>(qs)
    } catch (error) {
      if ((error as Error & { engineUnavailable?: boolean }).engineUnavailable) {
        return { threads: [], world_candidates: [], engineUnavailable: true }
      }
      throw error
    }
  }

  /** The Saved-for-later shelf: threads snoozed via "Later" — recallable. */
  async getSaved(): Promise<{ threads: OpenThread[]; engineUnavailable?: boolean }> {
    try {
      return await this.request<{ threads: OpenThread[] }>('/saved')
    } catch (error) {
      if ((error as Error & { engineUnavailable?: boolean }).engineUnavailable) {
        return { threads: [], engineUnavailable: true }
      }
      throw error
    }
  }

  /** Held threads + noticed people/places for ONE conversation (shown in-chat). */
  async getConversationInbox(chatId: string): Promise<GuidanceInbox> {
    try {
      return await this.request<GuidanceInbox>(
        `/conversation-inbox?chat_id=${encodeURIComponent(chatId)}`,
      )
    } catch (error) {
      if ((error as Error & { engineUnavailable?: boolean }).engineUnavailable) {
        return { threads: [], world_candidates: [], engineUnavailable: true }
      }
      throw error
    }
  }

  /** Which held/saved thoughts do these just-saved/completed items move
   *  forward? Fire-and-forget nicety — any failure resolves to []. */
  async getConnections(
    items: Array<{ type: string; title: string }>,
  ): Promise<GuidanceConnection[]> {
    if (!items.length) return []
    try {
      const data = await this.request<{ connections: GuidanceConnection[] }>('/connections', {
        method: 'POST',
        body: JSON.stringify({ items: items.slice(0, 10) }),
      })
      return Array.isArray(data.connections) ? data.connections : []
    } catch {
      return []
    }
  }

  threadAction(
    threadId: string,
    action: 'explore' | 'snooze' | 'unsnooze' | 'dismiss' | 'trash' | 'connect',
    extra: { snooze_days?: number; node_ids?: string[]; entity_ids?: string[] } = {},
  ): Promise<{ ok: boolean }> {
    return this.request(`/threads/${threadId}/action`, {
      method: 'POST',
      body: JSON.stringify({ action, ...extra }),
    })
  }

  convertThread(
    threadId: string,
    extra: { node_type?: string; title?: string } = {},
  ): Promise<{ ok: boolean; node_id: string | null; patch_id: string }> {
    return this.request(`/threads/${threadId}/convert`, {
      method: 'POST',
      body: JSON.stringify(extra),
    })
  }

  listWorld(): Promise<{ entities: WorldCandidate[] }> {
    return this.request('/world')
  }

  worldAction(
    entityId: string,
    action: 'confirm' | 'dismiss' | 'edit' | 'merge',
    extra: { name?: string; relationship?: string; place_kind?: string; merge_into_id?: string } = {},
  ): Promise<{ ok: boolean; node_id?: string | null }> {
    return this.request(`/world/${entityId}/action`, {
      method: 'POST',
      body: JSON.stringify({ action, ...extra }),
    })
  }

  /** The "How yanamas helps me" calibration profile (facets + overrides). */
  async getProfile(): Promise<GuidanceProfile> {
    try {
      return await this.request<GuidanceProfile>('/profile')
    } catch (error) {
      if ((error as Error & { engineUnavailable?: boolean }).engineUnavailable) {
        return { facets: [], domain_overrides: [], engineUnavailable: true }
      }
      throw error
    }
  }

  /** Explicit facet edit — the engine rejects unknown facet/value pairs. */
  setProfileFacet(
    facet: string,
    value: string,
  ): Promise<{ ok: boolean; facet: string; value: string }> {
    return this.request('/profile/set', {
      method: 'POST',
      body: JSON.stringify({ facet, value }),
    })
  }

  /** Reset all calibration back to yanamas' defaults. */
  resetProfile(): Promise<{ ok: boolean }> {
    return this.request('/profile/reset', { method: 'POST', body: JSON.stringify({}) })
  }

  /** Behavioral breadcrumbs (Map-now clicks, dismissals, saves). Strictly
   *  fire-and-forget: swallows every failure so call sites can `void` it
   *  without try/catch and never block on the result. */
  async sendProfileEvents(events: GuidanceProfileEvent[]): Promise<void> {
    if (!events.length) return
    try {
      await this.request('/profile-events', {
        method: 'POST',
        body: JSON.stringify({ events: events.slice(0, 10) }),
      })
    } catch {
      /* a lost breadcrumb, never a caller's problem */
    }
  }

  respondToSuggestion(
    suggestionKey: string,
    response: 'acted_on' | 'dismissed' | 'snoozed',
    snoozeDays?: number,
  ): Promise<{ ok: boolean }> {
    return this.request('/suggestions/respond', {
      method: 'POST',
      body: JSON.stringify({
        suggestion_key: suggestionKey,
        response,
        ...(snoozeDays ? { snooze_days: snoozeDays } : {}),
      }),
    })
  }
}

export const hortonGuidanceService = new HortonGuidanceService()

// Completion surface ("completing this also helps X"): one guarded lookup for
// the first genuine connection to a held/saved thought. Returns null on
// error/empty/in-flight — callers toast only when something real came back.
let completionCheckInFlight = false
export async function connectionForCompletedItem(item: {
  type: string
  title: string
}): Promise<GuidanceConnection | null> {
  if (completionCheckInFlight || !item.title) return null
  completionCheckInFlight = true
  try {
    const connections = await hortonGuidanceService.getConnections([item])
    return connections[0] ?? null
  } catch {
    return null
  } finally {
    completionCheckInFlight = false
  }
}
